import { useCallback, useState } from 'react';

import { formatCurrency, formatDate } from '../lib/format';
import { useAdminData } from '../lib/use-admin-data';
import {
  fetchAdminAccesses,
  fetchAdminCreators,
  type AccessEntry,
  type AdminFilters,
  type CreatorStat,
} from '../services/api';
import { Feedback } from './Feedback';

/**
 * Aba ACESSOS — o registro cru, requisição por requisição, e o uso por criador.
 *
 * É a aba de investigação: quando algo quebra em produção, o `requestId` desta
 * tabela é a mesma chave que aparece nos logs da API e no cabeçalho
 * `X-Request-Id` devolvido ao usuário.
 */

interface AdminAccessesTabProps {
  filters: AdminFilters;
}

const REFRESH_MS = 5_000;
const ROWS = 60;

export function AdminAccessesTab({ filters }: AdminAccessesTabProps) {
  const [onlyErrors, setOnlyErrors] = useState(false);

  const accessesLoader = useCallback(
    () => fetchAdminAccesses(filters, { limit: ROWS, onlyErrors }),
    [filters, onlyErrors],
  );
  const creatorsLoader = useCallback(() => fetchAdminCreators(filters), [filters]);

  const accesses = useAdminData<{ accesses: AccessEntry[] }>(accessesLoader, REFRESH_MS);
  const creators = useAdminData<{ creators: CreatorStat[] }>(creatorsLoader);

  const error = accesses.error ?? creators.error;
  if (error) return <Feedback kind="error">{error}</Feedback>;

  return (
    <div className="admin-tab">
      <section className="admin-section">
        <div className="admin-section__header">
          <h3 className="admin-section__title">Últimos acessos</h3>
          <label className="form__label form__label--checkbox">
            <input
              type="checkbox"
              checked={onlyErrors}
              onChange={(event) => setOnlyErrors(event.target.checked)}
            />
            Só erros
          </label>
        </div>
        <p className="admin-section__description">
          Atualiza a cada {REFRESH_MS / 1000}s. O caminho aparece normalizado — os tokens do overlay
          e do moderador nunca são gravados, porque a URL deles é a própria credencial.
        </p>

        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Quando</th>
                <th scope="col">Rota</th>
                <th scope="col">Status</th>
                <th scope="col">Tempo</th>
                <th scope="col">Origem</th>
                <th scope="col">Cliente</th>
              </tr>
            </thead>
            <tbody>
              {(accesses.data?.accesses ?? []).map((access) => (
                <tr key={`${access.createdAt}-${access.requestId ?? access.route}`}>
                  <td>{formatDate(access.createdAt)}</td>
                  <td>
                    <code>
                      {access.method} {access.route}
                    </code>
                    {access.isSynthetic && <span className="tag tag--synthetic">teste</span>}
                  </td>
                  <td>
                    <span className={`status-pill status-pill--${statusClass(access.status)}`}>
                      {access.status}
                    </span>
                  </td>
                  <td className="data-table__number">{access.durationMs} ms</td>
                  <td>
                    {[access.city, access.country].filter(Boolean).join(', ') || access.ip || '—'}
                    {access.origin && <span className="data-table__muted"> · {access.origin}</span>}
                  </td>
                  <td className="data-table__muted">
                    {[access.browser, access.os].filter(Boolean).join(' · ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="admin-section">
        <h3 className="admin-section__title">Criadores</h3>
        <p className="admin-section__description">
          Visitas contam os acessos às páginas do criador no período; mensagens e valores são o
          total histórico, já sem o tráfego do teste de carga.
        </p>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Criador</th>
                <th scope="col">Visitas</th>
                <th scope="col">Visitantes</th>
                <th scope="col">Mensagens</th>
                <th scope="col">Recebido</th>
                <th scope="col">Desde</th>
              </tr>
            </thead>
            <tbody>
              {(creators.data?.creators ?? []).map((creator) => (
                <tr key={creator.id}>
                  <td>
                    {creator.name} <code>/{creator.username}</code>
                  </td>
                  <td className="data-table__number">{creator.requests.toLocaleString('pt-BR')}</td>
                  <td className="data-table__number">{creator.visitors.toLocaleString('pt-BR')}</td>
                  <td className="data-table__number">{creator.messages.toLocaleString('pt-BR')}</td>
                  <td className="data-table__number">{formatCurrency(creator.revenueCents)}</td>
                  <td>{formatDate(creator.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

/** Classe visual da resposta HTTP (a cor nunca vai sozinha — o número aparece). */
function statusClass(status: number): 'ok' | 'warning' | 'critical' {
  if (status >= 500) return 'critical';
  if (status >= 400) return 'warning';
  return 'ok';
}
